import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Loader2, CheckCircle2, FileCheck2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { BlogAPI } from '../../lib/api';

// Lead-gated download: short form -> stores lead -> reveals PDF download.
const BlogLeadForm = ({ slug, download }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: 'Missing fields', description: 'Please enter your name and email.' });
      return;
    }
    setLoading(true);
    try {
      const res = await BlogAPI.createLead({ slug, ...form });
      setResult(res);
      toast({ title: 'Your download is ready', description: download?.title || 'Enjoy the resource.' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="lead-form"
      className="rounded-2xl border-2 border-[#0A2A63]/10 bg-white p-6 shadow-lg scroll-mt-28"
      data-testid="blog-lead-form"
    >
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 rounded-xl bg-[#FF4E50]/15 flex items-center justify-center flex-shrink-0">
          <FileCheck2 className="h-5 w-5 text-[#FF4E50]" />
        </span>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4E50] font-black">
            {download?.kind === 'scorecard' ? 'Free scorecard' : 'Free checklist'}
          </p>
          <h3 className="text-lg font-black text-[#050F2E] leading-tight mt-0.5">{download?.title}</h3>
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-3 leading-relaxed">{download?.intro || 'Get the printable resource for your team.'}</p>

      {result ? (
        <div className="mt-5" data-testid="blog-download-ready">
          <div className="flex items-center gap-2 text-[#0F7B3F] font-semibold text-sm mb-3">
            <CheckCircle2 className="h-4 w-4" /> Thanks, {form.name.split(' ')[0] || 'there'}! Your file is ready.
          </div>
          <a href={result.download_url} target="_blank" rel="noreferrer" download>
            <Button
              className="w-full bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 font-bold btn-transition"
              data-testid="blog-download-button"
            >
              <Download className="h-4 w-4 mr-2" />
              {result.download_label || download?.label || 'Download'}
            </Button>
          </a>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <Input
            placeholder="Full name"
            value={form.name}
            onChange={setField('name')}
            className="h-11 rounded-xl"
            data-testid="blog-lead-name"
          />
          <Input
            type="email"
            placeholder="Work email"
            value={form.email}
            onChange={setField('email')}
            className="h-11 rounded-xl"
            data-testid="blog-lead-email"
          />
          <Input
            placeholder="Company (optional)"
            value={form.company}
            onChange={setField('company')}
            className="h-11 rounded-xl"
            data-testid="blog-lead-company"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF4E50] hover:bg-[#FF7273] text-[#050F2E] rounded-full h-12 font-black btn-transition"
            data-testid="blog-lead-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Get the {download?.kind === 'scorecard' ? 'scorecard' : 'checklist'}
          </Button>
          <p className="text-[11px] text-slate-400 leading-snug">
            We&apos;ll email you occasional Proof Nation insights. Unsubscribe anytime.
          </p>
        </form>
      )}
    </div>
  );
};

export default BlogLeadForm;
